import { useMemo, useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import { estimateMissing } from '@/engine/estimate';
import { inputClass } from '@/components/ui/Field';
import { nowIso, putAndQueue, type FoodRow } from '@/lib/db';
import { searchFoods } from '@/lib/search';
import { upsertCatalogFood, useCatalog } from '@/lib/useCatalog';

interface Props {
  food: FoodRow;
  onDone: (food: FoodRow) => void;
  onClose: () => void;
}

/**
 * Vincular un producto escaneado a un genérico para estimar lo que su etiqueta
 * no declara (SPEC 5.4).
 *
 * La etiqueta de un yogur trae ocho valores; el resto de micros no existen en
 * Open Food Facts porque el fabricante no está obligado a declararlos. Sin
 * esto, comer a diario un producto envasado hunde la cobertura del día aunque
 * el yogur sí lleve calcio.
 *
 * Es SIEMPRE una acción del usuario: la app no vincula nada por su cuenta, ni
 * adivina el genérico. Estimar sin que nadie lo pida es inventar datos.
 */
export function EstimateMicros({ food, onDone, onClose }: Props) {
  const { index, byId } = useCatalog();
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);

  const results = useMemo(() => {
    if (!index || query.trim().length < 2) return [];
    return searchFoods(index, query, { limit: 8 })
      .map((id) => byId.get(id))
      .filter((f): f is FoodRow => f != null && f.kind === 'generico');
  }, [index, query, byId]);

  async function link(generic: FoodRow) {
    if (saving) return;
    setSaving(true);
    try {
      const { per100g, sources } = estimateMissing(food, generic);
      const row: FoodRow = {
        ...food,
        per100g,
        sources,
        estimatedFrom: { id: generic.id, name: generic.name },
        updatedAt: nowIso(),
      };
      await putAndQueue('foods', row.id, row);
      upsertCatalogFood(row);
      onDone(row);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mt-3 rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3">
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-sm font-semibold">Estimar lo que falta</h3>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cancelar la estimación"
          className="tap -mt-1 -mr-1 rounded-lg px-2 text-[var(--color-text-muted)]"
        >
          <X aria-hidden="true" className="size-4" />
        </button>
      </div>

      <p className="mt-1 text-xs text-[var(--color-text-muted)]">
        Elige un alimento genérico parecido. Se copiarán solo los valores que la etiqueta no trae, y
        quedarán marcados como estimados.
      </p>

      <label htmlFor="generico" className="sr-only">
        Buscar genérico parecido
      </label>
      <input
        id="generico"
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Yogur natural, lentejas cocidas…"
        className={`${inputClass} mt-2`}
      />

      {results.length > 0 ? (
        <ul className="mt-2 flex flex-col gap-1">
          {results.map((generic) => (
            <li key={generic.id}>
              <button
                type="button"
                disabled={saving}
                onClick={() => void link(generic)}
                className="tap flex w-full items-center gap-2 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-left text-sm disabled:opacity-60"
              >
                <Sparkles aria-hidden="true" className="size-4 text-[var(--color-brand-strong)]" />
                <span className="min-w-0 flex-1 truncate">{generic.name}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
